import React, { FC, useEffect, useRef, useState } from 'react';

import { LANDING } from '../../configs/api/api.config';
import { useCameraPreview } from '../../hooks/useCameraPreview';
import { IClient, IConsultation } from '../../store/types';
import { getDate, getTime } from '../../utils/getTime';
import { Button, Spinner, Heading, Pressable, TextLink } from '../../shared/ui';
import Header from '../header';
import { VideoIcon } from '../icons/video';
import { Settings } from '../icons/settings';
import { NetworkCheckState, useVpnDetection } from '../../hooks/useVpnDetection';
import { usePosthogCapture } from '../../hooks/usePosthogCapture';
import { useLobbyBackgroundBlur } from '../../hooks/useLobbyBackgroundBlur';

import { LobbyBlurToggle } from './lobby-blur-toggle';
import { VpnWarningBanner } from './vpn-warning-banner';

type PsychologJoinProps = {
  consultation?: IConsultation;
  client: IClient | undefined;
  setStarted: (val: boolean, isCameraEnabled?: boolean) => void | Promise<void>;
  setOpenSettings: (val: boolean) => void | Promise<void>;
  users: any[];
  disabled?: boolean;
  isRoomInitialized?: boolean | null;
  /** When true, show "Групповая встреча" and optional "до N участников" */
  isGroupConsultation?: boolean;
  livekitToken: string | null;
  networkCheckState: NetworkCheckState;
};

export const PsychologJoin: FC<PsychologJoinProps> = ({
  consultation,
  client,
  setStarted,
  setOpenSettings,
  users,
  isRoomInitialized,
  disabled,
  isGroupConsultation = false,
  livekitToken,
  networkCheckState,
}) => {
  const [isJoining, setIsJoining] = useState(false);
  const [vpnDismissed, setVpnDismissed] = useState(false);
  const { isVpn, isChecking, isUnavailable, flags } = useVpnDetection(livekitToken, networkCheckState);
  const capture = usePosthogCapture();
  const vpnEventSentRef = useRef(false);

  useEffect(() => {
    if (isChecking || vpnEventSentRef.current) return;
    vpnEventSentRef.current = true;
    capture('meet_join_vpn_status', {
      is_vpn: isVpn,
      network_check_available: !isUnavailable,
      role: 'psychologist',
      ...flags,
    });
  }, [capture, flags, isChecking, isUnavailable, isVpn]);

  const { videoRef, stream, isLoading, cameraError, isCameraEnabled, toggleCamera, stopStream } = useCameraPreview();
  const blur = useLobbyBackgroundBlur(stream ?? null, videoRef, isCameraEnabled);

  const handleJoin = async () => {
    stopStream();
    setIsJoining(true);
    await setStarted(true, isCameraEnabled);
  };

  const psychologistAlreadyIn = users.some((el) => el.display?.split('-').at(-1)?.trim() === 'psychologist');
  const isOnlyClient = users.length === 1 && users[0]?.display?.split('-').at(-1)?.trim() === 'client';

  return (
    <>
      <div className="fixed w-full h-screen z-modal bg-white flex flex-col items-center">
        <Header />
        <div className="w-full shrink-0 h-[50px] lg:h-[80px]" />
        {isVpn && !vpnDismissed && <VpnWarningBanner onDismiss={() => setVpnDismissed(true)} />}

        {consultation && client && (
          <div className="w-full px-[15px] flex flex-col items-center overflow-y-auto">
            <div className="w-full flex flex-col items-center py-[30px]">
              <Heading size="xl" className="flex flex-col mb-7 text-center">
                <span>ZiNGiZi</span>
                <span>{isGroupConsultation ? 'Групповая встреча' : 'Консультация'}</span>
              </Heading>
              {isGroupConsultation && <div className="text-ink-subtle text-ui mb-2">до 20 участников</div>}
              {isOnlyClient && (
                <div className="p-[5px_20px] rounded-control bg-success-surface text-success mb-[15px]">
                  Клиент ожидает
                </div>
              )}

              <div className="flex flex-col mb-5 gap-[8px]">
                <div className="flex gap-[15px]">
                  <span className="text-ink-subtle">Дата:</span>
                  <span className="text-ink">{getDate(new Date(consultation?.date))}</span>
                </div>
                <div className="flex gap-[15px]">
                  <span className="text-ink-subtle">Время:</span>
                  <span className="text-ink">{`${
                    consultation?.datetime_start && getTime(new Date(consultation?.datetime_start))
                  } - ${consultation?.datetime_end && getTime(new Date(consultation?.datetime_end))}`}</span>
                </div>
                <div className="flex gap-[15px]">
                  <span className="text-ink-subtle">Клиент:</span>
                  <span className="max-w-[160px] text-ink">{client?.full_name}</span>
                </div>
                <div className="flex gap-[15px]">
                  <span className="text-ink-subtle">Сессия:</span>
                  <span className="text-ink">{`№${consultation.consultation_number}`}</span>
                </div>
              </div>
              <p className="text-center text-ink-muted text-ui lg:text-body-l mb-2 max-w-[344px]">
                Доступ к инструментам психолога в мобильной версии отключен
              </p>

              <div className="w-full max-w-[344px] sm:max-w-[390px] mb-5 rounded-card overflow-hidden bg-ink relative aspect-video shadow-raised">
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover -scale-x-100"
                />

                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-ink">
                    <Spinner size="lg" className="text-white" />
                  </div>
                )}

                {cameraError && !isLoading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-ink text-white text-center px-4 gap-2">
                    <VideoIcon checked width={40} height={40} />
                    <p className="text-ui-xs leading-relaxed">{cameraError}</p>
                  </div>
                )}

                {!isCameraEnabled && !cameraError && !isLoading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-ink text-white gap-2">
                    <VideoIcon checked width={40} height={40} />
                    <p className="text-ui-xs">Камера выключена</p>
                  </div>
                )}

                <div className="absolute bottom-2 left-2 flex gap-2">
                  <Pressable
                    aria-label={isCameraEnabled ? 'Выключить камеру' : 'Включить камеру'}
                    aria-pressed={isCameraEnabled}
                    disabled={!!cameraError}
                    className="rounded-full disabled:opacity-40"
                    onClick={toggleCamera}
                  >
                    <VideoIcon checked={!isCameraEnabled} width={36} height={36} />
                  </Pressable>
                  <LobbyBlurToggle
                    isBlurEnabled={blur.isBlurEnabled}
                    toggleBlur={blur.toggleBlur}
                    isSupported={blur.isSupported}
                    isPreparing={blur.isPreparing}
                    disabled={!!cameraError || !isCameraEnabled}
                  />
                </div>
              </div>

              {psychologistAlreadyIn ? (
                <div className="text-white p-[10px_20px] rounded-control bg-primary mb-[15px]">
                  <span>У Вас уже есть активная сессия</span>
                </div>
              ) : (
                <Button
                  size="lg"
                  className="mb-5 w-[344px] max-w-[90%] sm:w-[390px]"
                  onClick={() => handleJoin()}
                  disabled={disabled || isJoining}
                  loading={isJoining || isRoomInitialized === null}
                >
                  {isJoining ? 'Подключение' : isRoomInitialized === null ? 'Инициализация' : 'Подключиться к сессии'}
                </Button>
              )}

              <small className="text-ink-subtle text-micro text-center max-w-[344px] sm:max-w-[390px] mb-5">
                Нажимая кнопку «Подключиться к сессии», я принимаю условия{' '}
                <TextLink
                  tone="inherit"
                  to={`${LANDING}/wp-content/uploads/2025/03/polzovatelskoe_soglashenie_logosterapi_rev.pdf`}
                  target="_blank"
                >
                  Пользовательского соглашения
                </TextLink>
                , даю
                <TextLink
                  tone="inherit"
                  to={`${LANDING}/wp-content/uploads/2025/03/politika_konfidenczialnosti_logosterapi_rev.pdf`}
                  target="_blank"
                >
                  согласие на обработку персональных данных
                </TextLink>
                и подтверждаю предоставление Психологу своего согласия на обработку моих персональных данных.
              </small>
              <p className="text-center text-ink-muted text-ui lg:text-body-l mb-[20px] lg:mb-[30px]">
                Проверьте настройки видео-конференции перед её началом.
              </p>
              <Button
                size="lg"
                className="w-[344px] max-w-[90%] sm:w-[390px]"
                variant="neutral"
                onClick={async () => {
                  await setOpenSettings(true);
                }}
                disabled={isJoining}
              >
                Настройки
                <Settings
                  needHover={false}
                  fill="none"
                  className="absolute top-[50%] right-[20px] -translate-y-1/2 z-1"
                />
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
